// eslint-disable-next-line max-classes-per-file
import type { DataV3Perk } from "@/libs/client/goApi/experience/postPreAllocateInterface";

export interface TicketAvailableDataPackageOptionName {
  en: string;
  th: string;
}

export class GetTicketAvailableDataPackageOptionName {
  en: string;

  th: string;

  constructor(response: TicketAvailableDataPackageOptionName) {
    this.en = response?.en ?? "";
    this.th = response?.th ?? "";
  }
}

export interface TicketAvailableDataPackageOptionCalendarPriceTier {
  id: string;
  price: number;
  originalPrice: number;
  available: number;
  currency: string;
  gotherPrice: number;
}

export class GetTicketAvailableDataPackageOptionCalendarPriceTier {
  id: string;

  price: number;

  originalPrice: number;

  gotherPrice: number;

  available: number;

  currency: string;

  constructor(response: TicketAvailableDataPackageOptionCalendarPriceTier) {
    this.id = response?.id ?? "";
    this.price = response?.price ?? 0;
    this.originalPrice = response?.originalPrice ?? 0;
    this.gotherPrice = response?.gotherPrice ?? 0;
    this.available = response?.available ?? 0;
    this.currency = response?.currency ?? "";
  }
}

export interface TicketAvailableDataPackageOptionCalendarSession {
  id: string;
  name: string;
  priceTiers: TicketAvailableDataPackageOptionCalendarPriceTier[];
}

export class GetTicketAvailableDataPackageOptionCalendarSession {
  id: string;

  name: string;

  priceTiers: TicketAvailableDataPackageOptionCalendarPriceTier[];

  constructor(response: TicketAvailableDataPackageOptionCalendarSession) {
    this.id = response?.id ?? "";
    this.name = response?.name ?? "";
    this.priceTiers = (response?.priceTiers ?? []).map(
      (res) => new GetTicketAvailableDataPackageOptionCalendarPriceTier(res),
    );
  }
}

export interface TicketAvailableDataPackageOptionCalendar {
  date: string;
  sessions: TicketAvailableDataPackageOptionCalendarSession[];

  priceTiers: TicketAvailableDataPackageOptionCalendarPriceTier[];
}

export class GetTicketAvailableDataPackageOptionCalendar {
  date: string;

  priceTiers: TicketAvailableDataPackageOptionCalendarPriceTier[];

  sessions: TicketAvailableDataPackageOptionCalendarSession[];

  constructor(response: TicketAvailableDataPackageOptionCalendar) {
    this.date = response?.date ?? "";
    this.priceTiers = (response?.priceTiers ?? []).map(
      (res) => new GetTicketAvailableDataPackageOptionCalendarPriceTier(res),
    );
    this.sessions = (response?.sessions ?? []).map(
      (res) => new GetTicketAvailableDataPackageOptionCalendarSession(res),
    );
  }
}

export interface TicketAvailableDataPackageOptionMeta {
  skuId: number;
}

export class GetTicketAvailableDataPackageOptionMeta {
  skuId: number;

  constructor(response: TicketAvailableDataPackageOptionMeta) {
    this.skuId = response?.skuId ?? 0;
  }
}
export interface TicketAvailableDataPackageOption {
  name: TicketAvailableDataPackageOptionName;
  minQuantity: number;
  unit: string;
  calendar: TicketAvailableDataPackageOptionCalendar[];
  lowestPriceEntry: TicketAvailableDataPackageOptionCalendarPriceTier;
  ticketType: string;
  meta: TicketAvailableDataPackageOptionMeta;
  increasingBy: number;
}

export class GetTicketAvailableDataPackageOption {
  name: TicketAvailableDataPackageOptionName;

  minQuantity: number;

  unit: string;

  lowestPriceEntry: GetTicketAvailableDataPackageOptionCalendarPriceTier;

  calendar: TicketAvailableDataPackageOptionCalendar[];

  ticketType: string;

  meta: TicketAvailableDataPackageOptionMeta;

  increasingBy: number;

  constructor(response: TicketAvailableDataPackageOption) {
    this.name = new GetTicketAvailableDataPackageOptionName(response?.name);
    this.minQuantity = response?.minQuantity ?? 0;
    this.unit = response?.unit ?? "";
    this.lowestPriceEntry =
      new GetTicketAvailableDataPackageOptionCalendarPriceTier(
        response?.lowestPriceEntry ?? {},
      );
    this.calendar = (response?.calendar ?? []).map(
      (res) => new GetTicketAvailableDataPackageOptionCalendar(res),
    );
    this.ticketType = response?.ticketType ?? "";
    this.meta = new GetTicketAvailableDataPackageOptionMeta(response?.meta);
    this.increasingBy = response?.increasingBy ?? 1;
  }
}

export interface TicketAvailableDataPackageItineraryDetail {
  photoPath?: string[];
  detail: string;
  day: number;
  time: string;
  addressDetail: string;
  airportId: number;
  countryId: number;
  cityId?: number;
  areaId?: number;
}

export class GetTicketAvailableDataPackageItineraryDetail {
  photoPath?: string[];

  detail: string;

  day: number;

  time: string;

  addressDetail: string;

  airportId: number;

  countryId: number;

  cityId?: number;

  areaId?: number;

  constructor(response: TicketAvailableDataPackageItineraryDetail) {
    this.photoPath = response?.photoPath ?? [];
    this.detail = response?.detail ?? "";
    this.day = response?.day ?? 0;
    this.time = response?.time ?? "";
    this.addressDetail = response?.addressDetail ?? "";
    this.airportId = response?.airportId ?? "";
    this.countryId = response?.countryId ?? 0;
    this.areaId = response?.areaId ?? 0;
  }
}
export interface TicketAvailableDataPackage {
  allocationKey: string;
  packageKey: string;
  name: string;
  description: string;
  instantConfirmation: boolean;
  inclusions: string;
  howToUse: string;
  termsAndConditions: string;
  currency: string;
  options: TicketAvailableDataPackageOption[];
  itineraryDetails: TicketAvailableDataPackageItineraryDetail[];
  duration: any;
  expirationDate: any;
  requiredFields: any[];
  round: string;
  perks: DataV3Perk;
  minTotal: number;
  maxTotal: number;
  redeemStart: string;
  redeemEnd: string;
}

export class GetTicketAvailableDataPackage {
  allocationKey: string;

  packageKey: string;

  name: string;

  description: string;

  instantConfirmation: boolean;

  inclusions: string;

  howToUse: string;

  termsAndConditions: string;

  currency: string;

  options: TicketAvailableDataPackageOption[];

  itineraryDetails: TicketAvailableDataPackageItineraryDetail[];

  duration: any;

  expirationDate: any;

  requiredFields: any[];

  perks: DataV3Perk;

  round: string;

  minTotal: number;

  maxTotal: number;

  redeemStart: string;

  redeemEnd: string;

  constructor(response: TicketAvailableDataPackage) {
    this.allocationKey = response?.allocationKey ?? "";
    this.packageKey = response?.packageKey ?? "";
    this.name = response?.name ?? "";
    this.description = response?.description ?? "";
    this.instantConfirmation = !!response?.instantConfirmation;
    this.inclusions = response?.inclusions ?? "";
    this.howToUse = response?.howToUse ?? "";
    this.termsAndConditions = response?.termsAndConditions ?? "";
    this.currency = response?.currency ?? "";
    this.duration = response?.duration ?? "";
    this.options = (response?.options ?? []).map(
      (data) => new GetTicketAvailableDataPackageOption(data),
    );
    this.itineraryDetails = (response.itineraryDetails ?? []).map(
      (res) => new GetTicketAvailableDataPackageItineraryDetail(res),
    );
    this.expirationDate = response?.expirationDate ?? "";
    this.requiredFields = response?.requiredFields ?? "";
    this.round = response?.round ?? "";
    this.perks = response?.perks ?? {
      isPerks: false,
      perkIds: [],
      benefits: [],
    };
    this.minTotal = response?.minTotal ?? 0;
    this.maxTotal = response?.maxTotal ?? 0;
    this.redeemStart = response?.redeemStart ?? "";
    this.redeemEnd = response?.redeemEnd ?? "";
  }
}
